import { OrGroups } from "../types/flowchart";
import { UIBacktestState } from "./configGenerator";

type DependencyMap = Map<string, Set<string>>;

export interface LegCycleDetectionResult {
  cycleLegIds: string[];
  message: string;
}

function extractLegReferences(rawLegIdx: unknown, allLegs: Set<string>): string[] {
  const refs = new Set<string>();

  const addCandidate = (candidate: unknown) => {
    if (typeof candidate === "number" && Number.isInteger(candidate) && candidate >= 1) {
      const legId = String(candidate - 1);
      if (allLegs.has(legId)) refs.add(legId);
      return;
    }

    if (typeof candidate === "string") {
      const trimmed = candidate.trim();
      if (!trimmed) return;
      if (!/^\d+$/.test(trimmed)) return;

      const legNumber = parseInt(trimmed, 10);
      if (legNumber < 1) return;
      const legId = String(legNumber - 1);
      if (allLegs.has(legId)) refs.add(legId);
    }
  };

  if (Array.isArray(rawLegIdx)) {
    for (const value of rawLegIdx) {
      addCandidate(value);
    }
  } else if (typeof rawLegIdx === "string" && rawLegIdx.includes(",")) {
    for (const value of rawLegIdx.split(",")) {
      addCandidate(value);
    }
  } else {
    addCandidate(rawLegIdx);
  }

  return Array.from(refs);
}

function extractSignalDependencies(groups: OrGroups, allLegs: Set<string>): Set<string> {
  const dependencies = new Set<string>();

  for (const andGroup of groups) {
    for (const signal of andGroup) {
      const refs = extractLegReferences(signal.params?.leg_idx, allLegs);
      for (const legId of refs) {
        dependencies.add(legId);
      }
    }
  }

  return dependencies;
}

function buildCombinedDependencies(state: UIBacktestState): DependencyMap {
  const dependencies: DependencyMap = new Map();
  const allLegs = new Set(state.legs.map((_, index) => String(index)));

  Array.from(allLegs).forEach((legId) => {
    dependencies.set(legId, new Set<string>());
  });

  for (let legIndex = 0; legIndex < state.legs.length; legIndex++) {
    const leg = state.legs[legIndex];
    const currentLegId = String(legIndex);
    const currentDependencies = dependencies.get(currentLegId)!;

    // Mirror backend combined graph input: entry signal dependencies.
    const entryDeps = extractSignalDependencies(leg.entryRules, allLegs);
    Array.from(entryDeps).forEach((dep) => {
      currentDependencies.add(dep);
    });

    // Mirror backend combined graph input: dynamic trade-condition dependencies.
    for (const tradeCondition of leg.tradeConditions) {
      if (!tradeCondition.dynamicEnabled || !tradeCondition.dynamicConditions) {
        continue;
      }

      for (const dynamicCondition of tradeCondition.dynamicConditions) {
        const refs = extractLegReferences(dynamicCondition.params?.leg_idx, allLegs);
        for (const dep of refs) {
          currentDependencies.add(dep);
        }
      }
    }
  }

  return dependencies;
}

function findFirstCycle(dependencies: DependencyMap): string[] | null {
  const visited = new Set<string>();
  const visiting = new Set<string>();
  const stack: string[] = [];

  const dfs = (legId: string): string[] | null => {
    if (visiting.has(legId)) {
      const cycleStart = stack.indexOf(legId);
      if (cycleStart === -1) {
        return [legId, legId];
      }
      return [...stack.slice(cycleStart), legId];
    }

    if (visited.has(legId)) {
      return null;
    }

    visiting.add(legId);
    stack.push(legId);

    const deps = dependencies.get(legId);
    if (deps) {
      for (const depLeg of Array.from(deps)) {
        const cycle = dfs(depLeg);
        if (cycle) {
          return cycle;
        }
      }
    }

    stack.pop();
    visiting.delete(legId);
    visited.add(legId);
    return null;
  };

  for (const legId of Array.from(dependencies.keys())) {
    const cycle = dfs(legId);
    if (cycle) {
      return cycle;
    }
  }

  return null;
}

export function detectLegCycle(state: UIBacktestState): LegCycleDetectionResult | null {
  if (state.legs.length <= 1) {
    return null;
  }

  const dependencies = buildCombinedDependencies(state);
  const cycle = findFirstCycle(dependencies);

  if (!cycle) {
    return null;
  }

  const cycleLabels = cycle.map((legId) => `Leg ${Number(legId) + 1}`);
  return {
    cycleLegIds: cycle,
    message: `Cycles detected in leg dependencies: ${cycleLabels.join(" -> ")}.`,
  };
}
